import React, { useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import WorkList from '../../../platform/components/WorkList';
import RunDetail from '../../../platform/components/RunDetail';
import definitionForRule from '../../../platform/workLayer/definitionForRule';
import useRuns from './shell/useRuns';
import useWorkflowDefinitions from './shell/useWorkflowDefinitions';
// CHA-587 — the admin gate and gateway call are shared with the Agents screen.
import { TENANT_ID } from './shell/workGateway';

// ============================================================================
// CHA-588 / W1b — Work list mounted in the InMocean shell (#/work)
// ============================================================================
// Thin tenant wrapper: it feeds the PLATFORM WorkList (src/platform/components/WorkList.jsx)
// from the SHARED useAgentTickets view model passed down by InMoceanDashboard — the SAME single
// subscription behind the sidebar badge, Today, and the Agents cockpit. No second Firestore
// subscription (CHA-239 / CHA-272 one-source-of-truth rule).
//
// All writes go through the auth-gated `yulieTicketAction` gateway — never direct client writes
// (Firestore rules already deny them, CHA-136 / CHA-133). This wrapper owns the tenantId and the
// gateway call so WorkList itself stays tenant-agnostic.

// CHA-621 — which definition a finding is sent to comes from the tenant's SEEDED definitions, by the
// finding's rule (was: one hardcoded past_cancel_chase / past_cancel pair, CHA-592 + CHA-597). Send is
// offered only when exactly one seeded definition binds the finding's rule; otherwise there is no
// button. That keeps CHA-597's point — the UI never offers a Send that cannot legitimately fire — while
// letting a second playbook (cost_sheet_gp_drift, CHA-620) work without a picker. The gateway's
// start_run rule lock (CHA-596) remains the authority on the server.

// Map a raw ticket doc to the WorkList item shape. Only the fields the list renders — keeps the
// platform surface decoupled from InMocean's full ticket document. `runId` gates the Send control:
// a finding that already started a run cannot start another (mirrors the gateway's one-run guard).
const toItem = (t) => ({
  kind: 'finding',
  id: t.id,
  po_no: t.po_no,
  rule: t.rule,
  state: t.state,
  ownerUid: t.ownerUid,
  labels: t.labels,
  evidence: t.evidence,
  runId: t.runId || null,
});

export default function WorkHome({ agentTickets, userProfile }) {
  const [acting, setActing] = useState(false);
  // Ref mirror of `acting` so a re-entrant call is rejected synchronously — a double-click fires the
  // second handler before React has re-rendered with the disabled buttons, so state alone can't gate it.
  const actingRef = useRef(false);
  // CHA-592 / W2b — the FIRST deliberate second live subscription (findings still come from the
  // shared useAgentTickets unchanged; runs are their own DISTINCT collection + state machine). Only
  // active while #/work is mounted; cleaned up on navigate-away.
  const { runs, loading: runsLoading, error: runsError } = useRuns();
  // CHA-621 — seeded definitions drive Send. Until they load (or if they fail) no finding shows Send.
  const { definitions, error: definitionsError } = useWorkflowDefinitions();
  const definitionIdFor = (finding) => definitionForRule(definitions, finding?.rule);
  const canSend = (finding) => definitionIdFor(finding) != null;
  // CHA-619 — the open run detail. Keep the id, not a snapshot, so the header reflects the live run.
  const [selectedRunId, setSelectedRunId] = useState(null);
  const selectedRun = selectedRunId ? runs.find((r) => r.id === selectedRunId) || null : null;
  // Only a run opens a detail; selecting a finding does nothing new here.
  const onSelect = (item) => { if (item && item.kind === 'run') setSelectedRunId(item.id); };
  const open = Array.isArray(agentTickets?.open) ? agentTickets.open : [];
  const closed = Array.isArray(agentTickets?.closed) ? agentTickets.closed : [];
  // Open (new + snoozed) and closed-this-week findings feed the grouped list, tagged kind='finding'.
  // ONE PO, ONE CARD (Ron, CHA-592): a finding that has started a run (runId set) is filtered OUT of
  // the finding display entirely — it is represented solely by its run in "Run · open", never as a
  // second card in Open / Snoozed / Closed. The filter lives here (tenant-side) so the platform
  // WorkList/groupFindings stay tenant-agnostic and dumb. Runs (kind='run') join the SAME queue.
  const findings = [...open, ...closed].map(toItem).filter((f) => !f.runId);
  const items = [...findings, ...runs];

  const callAction = async (payload) => {
    if (actingRef.current) return; // a call is already in flight — ignore the extra click
    actingRef.current = true;
    setActing(true);
    try {
      const fn = httpsCallable(getFunctions(), 'yulieTicketAction');
      // CHA-591 — yulieTicketAction resolves the tenant SERVER-SIDE, but a platform admin
      // (tenantId=null, e.g. the operator) must NAME the tenant; resolveTenant does not guess. This
      // file lives under src/tenants/inmocean/, so it names its own tenant. A tenant-scoped user's
      // value is ignored server-side, so this is safe for everyone. Do NOT copy this into platform code.
      await fn({ ...payload, tenantId: TENANT_ID });
    } catch (err) {
      console.error('yulieTicketAction failed', err);
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Action failed. Please try again.');
    } finally {
      actingRef.current = false;
      setActing(false);
    }
  };

  // Reopen (transition → open): the gateway clears the snooze/close bookkeeping and re-arms
  // detection. `to: 'open'` is the reopen contract (CHA-582 FINDING_TRANSITIONS).
  const onReopen = (finding) => callAction({ action: 'transition', ticketId: finding.id, to: 'open' });

  // Send-to-workflow (CHA-592 / W2b): create ONE run from the finding via the gateway's start_run,
  // with the one definition that binds the finding's rule (CHA-621). The gateway enforces one run per
  // finding (WorkList only offers Send when the finding has no runId) and the rule lock.
  const onSend = (finding) => {
    const definitionId = definitionIdFor(finding);
    if (!definitionId) return; // no (or an ambiguous) binding: nothing to start
    callAction({ action: 'start_run', ticketId: finding.id, definitionId });
  };

  const onAssign = (finding) => {
    // eslint-disable-next-line no-alert
    const input = window.prompt('Assign to (user id) — leave blank to unassign:', finding.ownerUid || '');
    if (input === null) return; // cancelled
    const ownerUid = input.trim() ? input.trim() : null;
    callAction({ action: 'assign', ticketId: finding.id, ownerUid });
  };

  const onSetLabels = (finding) => {
    const current = Array.isArray(finding.labels) ? finding.labels.join(', ') : '';
    // eslint-disable-next-line no-alert
    const input = window.prompt('Labels (comma-separated) — leave blank to clear:', current);
    if (input === null) return; // cancelled
    const labels = input.split(',').map((s) => s.trim()).filter(Boolean);
    callAction({ action: 'set_labels', ticketId: finding.id, labels });
  };

  // Surface EITHER subscription's loading/error rather than showing WorkList's empty state on a
  // still-loading or dead subscription (which would read as "no findings" — a lie). W1b did this for
  // the shared findings subscription (agentTickets); W2b extends it to the runs subscription, which
  // is the second source (CHA-592). If EITHER is loading → loading; if EITHER errored → alert; the
  // list renders only when BOTH are ready and neither errored.
  if (agentTickets?.loading || runsLoading) {
    return (
      <div className="border border-carbon-border bg-carbon-white px-5 py-6 text-sm text-carbon-gray-60" data-testid="work-loading">
        Loading the work queue…
      </div>
    );
  }
  if (agentTickets?.error || runsError) {
    return (
      <div role="alert" className="border border-carbon-red-60 bg-carbon-white px-5 py-6 text-sm text-carbon-red-60" data-testid="work-error">
        {agentTickets?.error || runsError}
      </div>
    );
  }

  return (
    <div aria-busy={acting} className="space-y-4">
      {selectedRun && (
        // Read-only history of the run; this shell names its own tenant (the platform view never guesses).
        // keyed on tenant + run: a switch remounts the detail, so not even one frame of the previous
        // run's trail can render under the new header.
        <RunDetail key={`${TENANT_ID}/${selectedRun.id}`} tenantId={TENANT_ID} run={selectedRun} onClose={() => setSelectedRunId(null)} />
      )}
      {definitionsError && (
        // Send is hidden while definitions are unavailable (fail closed). Say so, so an empty Send
        // column is not read as "no playbook exists for these findings". The list stays usable.
        <div role="status" className="border border-carbon-border bg-carbon-gray-10 px-4 py-2 text-sm text-carbon-gray-70" data-testid="work-definitions-error">
          Send is unavailable right now: {definitionsError}
        </div>
      )}
      <WorkList findings={items} busy={acting} onSelect={onSelect} onSend={onSend} canSend={canSend} onReopen={onReopen} onAssign={onAssign} onSetLabels={onSetLabels} />
    </div>
  );
}
